import { reactive, ref } from 'vue'
import { invitesApi } from '@/api/invites'
import { apiErrorMessage } from '@/lib/errors'
import { withAsyncState } from '@/lib/asyncState'
import type { Invite } from '@/types/models'

// Singleton, following useGithubInstallations.ts's shape.
const invites = ref<Invite[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

const submitting = ref(false)
const submitError = ref<string | null>(null)
const fieldErrors = ref<Record<string, string[]>>({})

// Per-row action state, keyed by invite id, so one invite's resend/revoke
// spinner doesn't disable every other row's buttons.
const rowError = reactive<Record<string, string | null>>({})
const rowBusy = reactive<Record<string, boolean>>({})

export function useInvites() {
  async function fetch(): Promise<void> {
    await withAsyncState(
      error,
      'Failed to load invites.',
      async () => {
        invites.value = await invitesApi.index()
      },
      loading,
    )
  }

  async function create(email: string): Promise<boolean> {
    const result = await withAsyncState(
      submitError,
      'Failed to send invite.',
      async () => {
        const invite = await invitesApi.store({ email })
        invites.value = [invite, ...invites.value]
        return true
      },
      submitting,
      fieldErrors,
    )
    return result === true
  }

  async function resend(invite: Invite): Promise<void> {
    rowError[invite.id] = null
    rowBusy[invite.id] = true
    try {
      const updated = await invitesApi.resend(invite.id)
      invites.value = invites.value.map((current) => (current.id === updated.id ? updated : current))
      // Clear this row's state on success instead of leaving stale entries
      // behind — mirrors revoke()'s cleanup below.
      delete rowBusy[invite.id]
      delete rowError[invite.id]
    } catch (e: unknown) {
      rowError[invite.id] = apiErrorMessage(e, 'Failed to resend invite.')
      rowBusy[invite.id] = false
    }
  }

  async function revoke(invite: Invite): Promise<void> {
    rowError[invite.id] = null
    rowBusy[invite.id] = true
    try {
      await invitesApi.revoke(invite.id)
      invites.value = invites.value.map((current) =>
        current.id === invite.id ? { ...current, status: 'revoked' } : current,
      )
      // Revoked rows lose their action buttons for good, so this key is never
      // read again; delete it here instead of leaving it in the finally block.
      delete rowBusy[invite.id]
      delete rowError[invite.id]
    } catch (e: unknown) {
      rowError[invite.id] = apiErrorMessage(e, 'Failed to revoke invite.')
      rowBusy[invite.id] = false
    }
  }

  // Called on logout so the next session's invites list doesn't briefly show
  // the previous user's data before its own fetch resolves.
  function reset(): void {
    invites.value = []
    loading.value = false
    error.value = null
    submitting.value = false
    submitError.value = null
    fieldErrors.value = {}
    for (const key of Object.keys(rowError)) delete rowError[key]
    for (const key of Object.keys(rowBusy)) delete rowBusy[key]
  }

  return {
    invites,
    loading,
    error,
    submitting,
    submitError,
    fieldErrors,
    rowError,
    rowBusy,
    fetch,
    create,
    resend,
    revoke,
    reset,
  }
}
